interface WaveformRenderOptions {
  width: number
  height: number
  viewStart?: number // seconds
  viewEnd?: number // seconds
  backgroundColor?: string
  outlineColor?: string
  peakColor?: string
  coreGradient?: { top: string; middle: string; bottom: string }
}

export function setupHighDPICanvas(canvas: HTMLCanvasElement, width: number, height: number): CanvasRenderingContext2D | null {
  const dpr = window.devicePixelRatio || 1
  canvas.width = Math.max(1, Math.floor(width * dpr))
  canvas.height = Math.max(1, Math.floor(height * dpr))
  canvas.style.width = `${width}px`
  canvas.style.height = `${height}px`
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.scale(dpr, dpr)
  return ctx
}

export function renderFairlightWaveform(
  ctx: CanvasRenderingContext2D,
  buffer: AudioBuffer,
  options: WaveformRenderOptions,
): void {
  const {
    width,
    height,
    viewStart = 0,
    viewEnd = buffer.duration,
    backgroundColor = 'transparent',
    outlineColor = 'rgba(255,255,255,0.08)',
    peakColor = '#67e8f9',
    coreGradient = {
      top: 'rgba(103, 232, 249, 0.35)',
      middle: 'rgba(91, 215, 255, 0.18)',
      bottom: 'rgba(18, 33, 52, 0.8)',
    },
  } = options

  ctx.clearRect(0, 0, width, height)

  if (backgroundColor !== 'transparent') {
    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, width, height)
  }

  const channelData = buffer.getChannelData(0)
  const sampleRate = buffer.sampleRate
  const startSample = Math.floor(viewStart * sampleRate)
  const endSample = Math.min(channelData.length, Math.floor(viewEnd * sampleRate))
  const samples = endSample - startSample
  if (samples <= 0) return

  const step = Math.max(1, Math.floor(samples / width))
  const halfHeight = height / 2

  const gradient = ctx.createLinearGradient(0, 0, 0, height)
  gradient.addColorStop(0, coreGradient.top)
  gradient.addColorStop(0.5, coreGradient.middle)
  gradient.addColorStop(1, coreGradient.bottom)

  ctx.lineWidth = 1
  ctx.strokeStyle = outlineColor
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(width, 0)
  ctx.lineTo(width, height)
  ctx.lineTo(0, height)
  ctx.closePath()
  ctx.stroke()

  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.moveTo(0, halfHeight)

  for (let x = 0; x < width; x++) {
    const sampleIndex = startSample + x * step
    let min = 1.0
    let max = -1.0
    for (let i = 0; i < step && sampleIndex + i < endSample; i++) {
      const v = channelData[sampleIndex + i]
      if (v < min) min = v
      if (v > max) max = v
    }
    const yMax = halfHeight - max * halfHeight
    const yMin = halfHeight - min * halfHeight
    ctx.lineTo(x, yMax)
    ctx.lineTo(x, yMin)
  }

  ctx.lineTo(width, halfHeight)
  ctx.lineTo(0, halfHeight)
  ctx.closePath()
  ctx.fill()

  ctx.strokeStyle = peakColor
  ctx.beginPath()
  for (let x = 0; x < width; x++) {
    const sampleIndex = startSample + x * step
    let peak = 0
    for (let i = 0; i < step && sampleIndex + i < endSample; i++) {
      const v = channelData[sampleIndex + i]
      const abs = Math.abs(v)
      if (abs > peak) peak = abs
    }
    const y = halfHeight - peak * halfHeight
    ctx.moveTo(x + 0.5, halfHeight)
    ctx.lineTo(x + 0.5, y)
  }
  ctx.stroke()
}
